import { API_BASES } from "@lib/constants";
import logger from "@lib/logger";
import { getRequest } from "@lib/network/request";
import { Track } from "@models/track";
import IAudioAdapter from "./IAudioAdapter";
import { downSampleBuffer } from "./utils";

/**
 * Audio adapter for playing full length sample files.
 */

export const DEFAULT_VOLUME = 0.5;
export default class BasicPlaybackAdapter implements IAudioAdapter {
	private _mediaElement: HTMLAudioElement;
	private _audioContext: AudioContext;
	private _track: any;
	private _pitchShift: number;
	private _bufferDownSampleRate: number;
	private _leftChannel: number[];
	private _rightChannel: number[];
	private _loadError: boolean;

	constructor(mediaElement: HTMLAudioElement, audioContext: AudioContext) {
		this._mediaElement = mediaElement;
		this._audioContext = audioContext;
		this._track = {};
		this._pitchShift = 0;
		this._bufferDownSampleRate = 4410;
		this._leftChannel = [];
		this._rightChannel = [];
		this._loadError = false;

		this.reset();
	}

	public async load(track: any): Promise<unknown> {
		this.reset();
		this._track = track;
		this._loadError = false;
		let errorObject;

		await this.getBufferFromUrl(this._track)
			.then((buffer: AudioBuffer) => {
				this._leftChannel = downSampleBuffer(
					{ buffer: buffer.getChannelData(0), sampleRate: this._audioContext.sampleRate, targetSampleRate: this._bufferDownSampleRate },
				);
				this._rightChannel = downSampleBuffer(
					{ buffer: buffer.getChannelData(1), sampleRate: this._audioContext.sampleRate, targetSampleRate: this._bufferDownSampleRate },
				);
				return true;
			})
			.catch((error) => {
				this._loadError = true;
				errorObject = error;
				return;
			});

		if (this._loadError) {
			return Promise.reject(errorObject);
		}

		return new Promise((resolve) => {
			// one of these events will let us know the track can be started
			// ios sometimes does not fire loadeddata
			this._mediaElement.onloadeddata = resolve;
			this._mediaElement.oncanplay = resolve;
			this._mediaElement.onprogress = resolve;

			this._mediaElement.load();
			this._mediaElement.src = this._track.sample_url;
			this.setPitchShift(this._pitchShift);
		});
	}

	public play(time: number) {
		this.setCurrentTime(time);
		return this._mediaElement.play();
	}

	public pause() {
		try {
			this._mediaElement.pause();
		} catch {
			// Do Nothing...
		}
	}

	public addEventListener(e: any, fn: any) {
		this._mediaElement.addEventListener(e, fn);
	}

	public setPitchShift(shift: number) {
		this._pitchShift = shift;
		this._mediaElement.playbackRate = shift + 1;
	}

	public setCurrentTime(time: number) {
		this._mediaElement.currentTime = time;
	}

	public getCurrentTime() {
		return this._mediaElement.currentTime;
	}

	public setVolume(volume: number) {
		this._mediaElement.volume = volume;
	}

	public getVolume() {
		return this._mediaElement.volume;
	}

	public getDuration() {
		return this._mediaElement.duration;
	}

	public reset() {
		this.pause();
		this.setCurrentTime(0);
		this._leftChannel = [];
		this._rightChannel = [];
		this._mediaElement.src = "";
	}

	getBufferFromUrl = (track: Track) => {
		return getRequest({
			url: track.sample_url,
			apiBase: API_BASES.api4,
			responseType: "arraybuffer",
			noAuthorization: true,
		}, false)
			.then((response) => response?.data)
			.then((audioBuffer) => {
				if (!audioBuffer.byteLength) { return Promise.reject("Empty buffer"); }
				return this.decodeAudioDataPromise(audioBuffer);
			})
			.catch((error) => {
				this._loadError = true;
				logger.error(error, `Failed to get track (id: ${track.id}) buffer from url: ${track.sample_url}`);
				return Promise.reject(error);
			});
	};

	decodeAudioDataPromise = (buffer: ArrayBuffer): Promise<AudioBuffer> => {
		return new Promise((resolve, reject) => {
			this._audioContext.decodeAudioData(buffer, resolve, reject);
		});
	};
}
